import { DatePicker, Text, useParsedSearchParams } from "@fidelix/fx-miranda";
import { t } from "@lingui/core/macro";
import { DateTime } from "luxon";
import { DateValue } from "react-aria-components";
import { i18n } from "@lingui/core";
import styled from "styled-components";
import {
  defaultReplayTimePeriodMillis,
  useHistoryReplayContext,
} from "./history-replay-context";
import { areSameDate } from "./history-replay.utils";
import { defaultLocale } from "../../utils/i18n";

export function HistoryReplayDatePicker() {
  const { parsedParams } = useParsedSearchParams({
    to: { type: "string", defaultValue: undefined },
    from: { type: "string", defaultValue: undefined },
  });
  const { setDomain, movePlayheadTo } = useHistoryReplayContext();

  const onChangeDate = (date?: string) => {
    if (!date) {
      return;
    }
    const dateTime = DateTime.fromISO(date);
    const isCurrentDate = areSameDate(dateTime, DateTime.now());

    const toMillis = isCurrentDate
      ? DateTime.now().toMillis()
      : dateTime.toMillis() + defaultReplayTimePeriodMillis;

    const fromMillis = isCurrentDate
      ? toMillis - defaultReplayTimePeriodMillis
      : dateTime.toMillis();

    const from = DateTime.fromMillis(fromMillis);
    const to = DateTime.fromMillis(toMillis);

    setDomain([from, to]);

    movePlayheadTo(from.toSeconds());
  };

  // Don't allow clicking on future dates
  const isDateUnavailable = (date: DateValue) => {
    const calendarDate = DateTime.fromObject({
      year: date.year,
      month: date.month,
      day: date.day,
    });
    return calendarDate >= DateTime.now();
  };

  const isCurrentDate =
    parsedParams.to &&
    areSameDate(DateTime.fromISO(parsedParams.to), DateTime.now());
  const dateText = isCurrentDate
    ? t`Last 24h`
    : DateTime.fromISO(parsedParams.to!).toUTC().toLocaleString();
  return (
    <Container>
      <DatePicker
        hideTimeZone={true}
        aria-label="replay-date-picker"
        date-test-id="replay-date-picker"
        onChange={onChangeDate}
        containerStyle={{
          height: 35,
          display: "flex",
          justifyContent: "center",
          width: 60,
          whiteSpace: "nowrap",
          paddingLeft: 15,
          borderTop: 0,
        }}
        customValue={
          <div
            style={{
              position: "relative",
              right: 28 + dateText.length * 2,
              bottom: 30,
            }}
          >
            <CustomValueContainer>
              <Text variant="bodyExtraSmall">{dateText}</Text>
            </CustomValueContainer>
          </div>
        }
        iconPosition="left"
        iconSize={32}
        isDateUnavailable={isDateUnavailable}
        locale={i18n.locale || defaultLocale}
      />
    </Container>
  );
}

const CustomValueContainer = styled.div`
  position: absolute;
  z-index: 500000;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50%;
`;
